import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Sparkles, ArrowLeft, Loader2 } from 'lucide-react';
import ThemeToggle from '../components/ThemeToggle';
import TarotCard from '../components/TarotCard';
import SiteFooter from '../components/SiteFooter';
import { format } from 'date-fns';
import { toast } from 'sonner';

const DailyReading = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [reading, setReading] = useState(null);
  const [loading, setLoading] = useState(true);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    fetchDailyReading();
  }, []);

  const fetchDailyReading = async () => {
    try {
      const response = await axios.get(`${API}/readings/daily`);
      setReading(response.data);
      
      // Auto-reveal after a moment
      setTimeout(() => {
        setRevealed(true);
      }, 800);
    } catch (error) {
      toast.error('Failed to load daily reading');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-[#F8F9FA]">
      {/* Navigation */}
      <nav className="glass-effect border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <button
            onClick={() => navigate('/dashboard')}
            data-testid="back-to-dashboard"
            className="flex items-center gap-2 text-zinc-400 hover:text-[#D4AF37] transition-colors"
          >
            <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
            <span>Dashboard</span>
          </button>
          <div className="flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-[#D4AF37]" strokeWidth={1.5} />
            <span className="text-2xl font-heading font-light">Seatask Arcane AI</span>
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle />
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-12" data-testid="daily-reading-page">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-24">
            <Loader2 className="w-12 h-12 text-[#D4AF37] animate-spin mb-6" />
            <p className="text-zinc-400 font-heading text-xl">Drawing your daily card...</p>
            <p className="text-zinc-500 text-sm mt-2">The mystical AI is preparing your guidance</p>
          </div>
        ) : reading ? (
          <div>
            <div className="text-center mb-12">
              <p className="text-xs tracking-[0.2em] uppercase text-zinc-500 mb-4">
                {format(new Date(), 'EEEE, MMMM d, yyyy')}
              </p>
              <h1 className="text-5xl font-heading font-light tracking-tighter mb-4">Daily Guidance</h1>
              <p className="text-zinc-400 text-lg">Your card for today's journey</p>
            </div>

            {/* Card Display */}
            <div className="flex justify-center mb-12">
              <div className="w-60 sm:w-72">
                <TarotCard card={reading.cards[0]} isRevealed={revealed} />
              </div>
            </div>

            {/* Card Name */}
            <div className="text-center mb-8">
              <h2 className="text-3xl font-heading font-medium mb-2">{reading.cards[0].name}</h2>
              {reading.cards[0].is_reversed && (
                <p className="text-[#D4AF37] text-sm tracking-wide">Reversed</p>
              )}
            </div>

            {/* Interpretation */}
            <div className="glass-effect rounded-lg p-8">
              <h3 className="text-2xl font-heading font-medium mb-4">Today's Message</h3>
              <p className="text-zinc-300 leading-relaxed whitespace-pre-wrap">{reading.interpretation}</p>
            </div>

            {/* Action */}
            <div className="flex justify-center mt-8">
              <button
                onClick={() => navigate('/dashboard')}
                data-testid="back-button"
                className="px-6 py-3 bg-[#D4AF37] text-[#050505] rounded-md hover:bg-[#F3E5AB] transition-all"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        ) : (
          <div className="text-center py-24">
            <p className="text-zinc-400">No daily reading available</p>
          </div>
        )}
      </div>
      <SiteFooter />
    </div>
  );
};

export default DailyReading;
